import React from 'react';
import { motion } from 'framer-motion';
import { AnimatedNumber } from '../AnimatedNumber';
import type { MainOption } from '../../types/pulse';
import type { Spotlight } from '../../data/results';
interface FindingSpotlightProps {
  spotlight: Spotlight;
  options: MainOption[];
  accent: string;
}
const ease = [0.22, 1, 0.36, 1] as const;
export function FindingSpotlight({
  spotlight,
  options,
  accent
}: FindingSpotlightProps) {
  const byId = (id: string) => options.find(o => o.id === id);
  if (spotlight.kind === 'versus') {
    const a = byId(spotlight.aId);
    const b = byId(spotlight.bId);
    if (!a || !b) return null;
    return <div data-id="element-564" data-mp="256eq5y5e377d">
        <p className="text-[15px] font-medium text-navy-900" data-id="element-565" data-mp="209ekhocgc6gs">{spotlight.prompt}</p>
        <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-end gap-3" data-id="element-566" data-mp="207bv96j4ihd4">
          {[a, b].map((o, i) => <React.Fragment key={o.id} data-id="element-567" data-mp="2xqmzklg3x8xv" data-mpi={i}>
              {i === 1 && <span className="mb-6 flex h-8 w-8 items-center justify-center rounded-full bg-mist-100 text-[11px] font-semibold text-slate-500" data-id="element-568" data-mp="2swr7fzmiygam">
                  vs
                </span>}
              <div className={i === 1 ? 'text-right' : ''} data-id="element-569" data-mp="1kk2ldhajhu2y">
                <p className="font-serif text-[52px] leading-none" style={{
              color: i === 0 ? accent : '#0F2442'
            }} data-id="element-570" data-mp="15uxz9p0mj5pk">
                  <AnimatedNumber value={o.peerPercent} delay={0.2 + i * 0.3} data-id="element-571" data-mp="1atrb7d09ionm" />%
                </p>
                <p className="mt-2 text-[13px] leading-snug text-slate-600" data-id="element-572" data-mp="wuhumc859sj4">{o.short}</p>
              </div>
            </React.Fragment>)}
        </div>
        <div className="mt-5 flex h-3 overflow-hidden rounded-full bg-mist-200" data-id="element-573" data-mp="mxn167qqaibp">
          <motion.div className="h-full" style={{
          backgroundColor: accent
        }} initial={{
          width: 0
        }} animate={{
          width: `${a.peerPercent / (a.peerPercent + b.peerPercent) * 100}%`
        }} transition={{
          duration: 1.2,
          delay: 0.3,
          ease
        }} data-id="element-574" data-mp="rw6pufwpi87r" />
          <div className="h-full flex-1 bg-navy-900" data-id="element-575" data-mp="2kt24z8hk4aiu" />
        </div>
        <p className="mt-2 text-[11px] text-slate-400" data-id="element-576" data-mp="2kud8r8b05i9y">Share of these two answers only</p>
      </div>;
  }
  if (spotlight.kind === 'balance') {
    const left = byId(spotlight.leftId);
    const right = byId(spotlight.rightId);
    if (!left || !right) return null;
    const tilt = Math.max(-12, Math.min(12, (right.peerPercent - left.peerPercent) * 0.8));
    const weight = (p: number) => 18 + p * 0.9;
    return <div data-id="element-577" data-mp="2ykgqpn2umptl">
        <svg viewBox="0 0 320 150" className="w-full" aria-hidden="true" data-id="element-578" data-mp="2ykqfx8g2ksy8">
          <polygon points="160,140 146,112 174,112" fill="#0F2442" data-id="element-579" data-mp="2ymt89z4p2y64" />
          <motion.g style={{
          originX: 0.5,
          originY: 1
        }} initial={{
          rotate: 0
        }} animate={{
          rotate: tilt
        }} transition={{
          duration: 1.6,
          delay: 0.4,
          type: 'spring',
          bounce: 0.3
        }} data-id="element-580" data-mp="2ts5tirzhti4q">
            <line x1="40" y1="108" x2="280" y2="108" stroke="#0F2442" strokeWidth="3" strokeLinecap="round" data-id="element-581" data-mp="3hjb8l4xm06dz" />
            <motion.circle cx="58" cy={108 - weight(left.peerPercent) / 2 - 2} r={weight(left.peerPercent) / 2} fill="#B6C4D3" initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-582" data-mp="3ps8nbscsvrup" />
            <motion.circle cx="262" cy={108 - weight(right.peerPercent) / 2 - 2} r={weight(right.peerPercent) / 2} fill={accent} initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} data-id="element-583" data-mp="7ndm5a0zbatc" />
          </motion.g>
        </svg>
        <div className="-mt-1 flex justify-between" data-id="element-584" data-mp="qckbvh06x2on">
          {[{
          o: left,
          label: spotlight.leftLabel,
          color: '#4A6489'
        }, {
          o: right,
          label: spotlight.rightLabel,
          color: accent
        }].map(({
          o,
          label,
          color
        }, i) => <div key={o.id} className={i === 1 ? 'text-right' : ''} data-id="element-585" data-mp="10bjdifmjfy25" data-mpi={i}>
              <p className="font-serif text-[40px] leading-none" style={{
            color
          }} data-id="element-586" data-mp="1x1mvpnunfcrr">
                <AnimatedNumber value={o.peerPercent} delay={0.4} data-id="element-587" data-mp="1s3tpiutciirh" />%
              </p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-navy-900" data-id="element-588" data-mp="26ry9e244x0bu">{label}</p>
              <p className="text-[12px] text-slate-500" data-id="element-589" data-mp="21slcspngoew2">{o.short}</p>
            </div>)}
        </div>
      </div>;
  }
  const sorted = [...options].sort((a, b) => b.peerPercent - a.peerPercent);
  const top = sorted[0];
  const rest = 100 - top.peerPercent;
  const r = 52;
  const c = 2 * Math.PI * r;
  return <div className="flex items-center gap-5" data-id="element-590" data-mp="1n507cp3463kz">
      <div className="relative h-[132px] w-[132px] shrink-0" data-id="element-591" data-mp="1j2n36540mdqb">
        <svg viewBox="0 0 132 132" className="h-full w-full -rotate-90" aria-hidden="true" data-id="element-592" data-mp="1j12bcn9358w5">
          <circle cx="66" cy="66" r={r} fill="none" stroke="#E1ECEC" strokeWidth="12" data-id="element-593" data-mp="2yhsx5uccghye" />
          <motion.circle cx="66" cy="66" r={r} fill="none" stroke={accent} strokeWidth="12" strokeLinecap="round" strokeDasharray={c} initial={{
          strokeDashoffset: c
        }} animate={{
          strokeDashoffset: c * (1 - top.peerPercent / 100)
        }} transition={{
          duration: 1.4,
          delay: 0.3,
          ease
        }} data-id="element-594" data-mp="2uh0nizzk8b1l" />
        </svg>
        <p className="absolute inset-0 flex items-center justify-center font-serif text-[34px] text-navy-900" data-id="element-595" data-mp="2x4b5ewrfybm0">
          <AnimatedNumber value={top.peerPercent} delay={0.3} data-id="element-596" data-mp="3pqw4yki2lu0n" />%
        </p>
      </div>
      <div data-id="element-597" data-mp="3psqfr5zmo5r0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{
        color: accent
      }} data-id="element-598" data-mp="3b1zmdtie0yls">
          Leading response
        </p>
        <p className="mt-1 font-serif text-[22px] leading-tight text-navy-900" data-id="element-599" data-mp="3g019cpixpi2j">{top.short}</p>
        <p className="mt-2 text-[13px] leading-snug text-slate-500" data-id="element-600" data-mp="7k81ez2o5ex3">
          {top.peerPercent < 50 ? `${top.short} led the responses — but the majority (${rest}%) selected other opportunities.` : `${top.short} was selected by a majority of respondents.`}
        </p>
      </div>
    </div>;
}